import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getArticle, updateArticle, getCategories } from '../../services/firestoreService';
import { uploadImage } from '../../utils/uploadImage';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTimes, faImage } from '@fortawesome/free-solid-svg-icons';
import PageHead from '../../components/common/PageHead';
import Alert from '../../components/common/Alert';

const EditArticle = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [editorKey, setEditorKey] = useState(Date.now());

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    metaDescription: '',
    featuredImage: null,
    category: '',
    ctaText: '',
    ctaHeading: '',
    ctaButtonText: '',
    ctaButtonLink: '',
    dateCreated: new Date(),
  });

  const [initialImage, setInitialImage] = useState('');
  const [imageName, setImageName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const article = await getArticle(articleId);
        setFormData({
          title: article.title || '',
          content: article.content || '',
          metaDescription: article.metaDescription || '',
          category: article.category || '',
          featuredImage: article.featuredImage || null,
          ctaText: article.ctaText || '',
          ctaHeading: article.ctaHeading || '',
          ctaButtonText: article.ctaButtonText || '',
          ctaButtonLink: article.ctaButtonLink || '',
        });
        setInitialImage(article.featuredImage || '');
        setImageName(article.featuredImage ? (article.featuredImage.split('/').pop() || 'Current Image') : '');
      } catch (err) {
        setError('Failed to load article. Please try again.');
      }
    };

    fetchArticle();
  }, [articleId]);

  useEffect(() => {
    const fetchCategories = async () => {
      const fetchedCategories = await getCategories();  
      setCategories(fetchedCategories);
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 4000);
      return () => clearTimeout(timer);
    }
    if (error) {
      const timer = setTimeout(() => setError(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'featuredImage') {
      setFormData({ ...formData, featuredImage: files[0] });
      setImageName(files[0]?.name || '');
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, featuredImage: null }));
    setInitialImage('');
    setImageName('');
  };

  const handleEditorChange = (content) => {
    setFormData((prev) => ({ ...prev, content }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);
    setError('');

    try {
      let imageUrl = formData.featuredImage;

      if (formData.featuredImage && typeof formData.featuredImage !== 'string') {
        imageUrl = await uploadImage(formData.featuredImage, 'article/featured_images/');
      }

      if (!formData.featuredImage && !initialImage) {
        imageUrl = '';
      }

      const ctaData = (formData.ctaText || formData.ctaHeading || formData.ctaButtonText || formData.ctaButtonLink)
        ? {
            ctaText: formData.ctaText,
            ctaHeading: formData.ctaHeading,
            ctaButtonText: formData.ctaButtonText,
            ctaButtonLink: formData.ctaButtonLink,
          }
        : {};

      const updatedArticleData = {
        title: formData.title,
        content: formData.content,
        metaDescription: formData.metaDescription,
        category: formData.category,
        featuredImage: imageUrl,
        ...ctaData,
      };

      await updateArticle(articleId, updatedArticleData);

      setSuccessMessage('Article updated successfully!');
      setTimeout(() => {
        navigate('/view-articles');
      }, 1000);
    } catch (err) {
      setError('Failed to update the article. Please try again.');
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className='max-w-screen overflow-hidden'>
      <PageHead name='Edit Article' />
      <div className='flex flex-col items-center justify-center my-16 mx-6'>

      <div className="w-full md:w-2/3">
          {error && <Alert type="error" message={error} />}
          {isUploading && <Alert type="info" message="Submitting article, please wait..." />}
          {successMessage && <Alert type="success" message={successMessage} />}
        </div>

        <form onSubmit={handleSubmit} 
          className="flex flex-col justify-center space-y-8 text-grey-600 w-full md:w-2/3"
          >
          <div>
            <label className="block text-gray-700 text-xs">Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full p-3 bg-transparent border border-gray-600 border-2 rounded-xs focus:outline-none placeholder-gray-500"
              required
              disabled={isUploading}
            />
          </div>

          <div>
            <div className='flex justify-between items-center'>
              <label className='text-xs'>Meta Description</label>
              <div className="text-xs text-gray-500">
                {formData.metaDescription.length}/160 characters
              </div>
            </div>
            <textarea
              name="metaDescription"
              placeholder="Meta Description"
              onChange={handleChange}
              value={formData.metaDescription}
              maxLength="160"
              className="w-full p-3 border bg-transparent border-gray-600 border-2 rounded-xs h-24 focus:outline-none placeholder-gray-500"
              disabled={isUploading}
            />
          </div>

          <div>
            <label className="text-xs">Category</label>
            <select
              name="category"
              onChange={handleChange}
              value={formData.category}
              className="w-full p-3 bg-transparent border border-gray-600 border-2 rounded-xs focus:outline-none text-gray-500"
              disabled={isUploading}
            >
              <option value="" disabled>
                Select Category
              </option>
              {categories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className='overflow-hidden'>
            <label className="text-xs">Featured Image</label>
            <label className="block p-3 bg-transparent border border-gray-600 border-2 rounded-xs text-gray-500 cursor-pointer">
              {imageName || initialImage ? (
                <div className="flex items-cente overflow-hidden">
                  <FontAwesomeIcon icon={faImage} className='text-lg mr-3' />
                  <span
                    className="truncate block align-middle max-w-[120px] sm:max-w-[220px] md:max-w-[320px] lg:max-w-[400px] px-1"
                    title={imageName || (initialImage && initialImage.split('/').pop())}
                  >
                    {imageName || (initialImage && initialImage.split('/').pop())}
                  </span>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="ml-2 text-red-500 hover:text-red-700"
                    disabled={isUploading}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ) : (
                <>
                  <FontAwesomeIcon icon={faUpload} className='text-lg mr-3' />
                  <span className="placeholder-gray-500">Upload Featured Image</span>
                </>
              )}
              <input
                type="file"
                name="featuredImage"
                accept="image/*"
                onChange={handleChange}
                className="sr-only"
                disabled={isUploading}
              />
            </label>
            {formData.featuredImage && typeof formData.featuredImage !== 'string' && (
              <img
                src={URL.createObjectURL(formData.featuredImage)}
                alt="Preview"
                className="h-20 mt-2"
              />
            )}
          </div>

          {['ctaHeading', 'ctaText', 'ctaButtonText', 'ctaButtonLink'].map((field) => (
            <div key={field}>
              <label className="text-xs">{field.replace(/cta/, 'CTA ').replace(/([A-Z])/g, ' $1')} (Optional)</label>
              <input
                type="text"
                name={field}
                placeholder={`Enter ${field}`}
                value={formData[field]}
                onChange={handleChange}
                className="w-full p-3 bg-transparent border border-gray-600 border-2 rounded-xs focus:outline-none placeholder-gray-500"
                disabled={isUploading}
              />
            </div>
          ))}

          <div className='mb-10 overflow-hidden'>
            <label className='text-xs'>Content</label>
            <ReactQuill
              key={editorKey}
              value={formData.content}
              theme='snow'
              required
              onChange={handleEditorChange}
              className="border border-gray-600 border-2 rounded-xs my-quill-editor"
              style={{ height: '100%', overflowY: 'auto' }}
              readOnly={isUploading}
            />
          </div>

          <div className='my-32 flex justify-center items-center'>
            <button
              type="submit"
              className='bg-primary text-white text-lg px-5 py-2 hover:bg-blue-600 rounded disabled:opacity-60'
              disabled={isUploading}
            >
              {isUploading ? 'Updating...' : 'Update Article'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditArticle;
